import React, { FC, useCallback, useContext, useEffect, useMemo } from 'react'
import { Box, ListItem, Typography } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import isEqual from 'lodash/isEqual'

import { ContractAlertDot } from '../Common/Selectors/ContractAlertDot'
import { displayStatusKeys } from '../../constants'
import { CurrentUserContext, useLimitedPerson, useTotalUnreadDocCount } from '../../hooks'
import {
  ActionSignatureStatus,
  BankableEntityContractType,
  ContractStatusType,
  Individual,
  LegalEntity,
  UboType,
  useActionSignatureRequestsCountQuery,
} from '../../graphql'
import { isActiveContract } from '../../utils'
import { dashboardClosedStatuses } from '../../utils/Data'
import { makeStyles } from '@material-ui/core/styles'

const useCompanySelectUnionStyles = makeStyles((theme) => ({
  item: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    rowGap: 0,
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2, 1, 6.625),
    '& .flag-icon-lg': {
      fontSize: '1.5em',
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1, 1, 1, 6.75),
    },
  },
  companyName: {
    position: 'relative',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    height: theme.spacing(4),
    lineHeight: '2rem',
    paddingRight: theme.spacing(1.65),
  },
  contractsSelectWrap: {
    width: '100%',
    boxShadow: 'none',
    paddingTop: theme.spacing(1),
    '&.Mui-expanded': {
      backgroundColor: '#F9F9F9',
    },
    '& .MuiAccordionSummary-expandIcon': {
      transform: 'rotate(-90deg)',
      '&.Mui-expanded': {
        transform: 'rotate(-180deg)',
      },
    },
    '& .MuiIconButton-label .MuiSvgIcon-root': {
      color: '#ccc',
    },
  },
  accordionHeader: {
    '&.Mui-expanded': {
      minHeight: theme.spacing(6),
    },
    '& .MuiAccordionSummary-content': {
      alignItems: 'center',
      margin: 0,
      '&.Mui-expanded': {
        margin: 0,
      },
      '& svg': {
        paddingLeft: theme.spacing(0.25),
      },
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0, 2),
    },
  },
  accordionBody: {
    flexDirection: 'column',
    padding: theme.spacing(0, 0, 1),
    '& .MuiList-root': {
      padding: 0,
      width: '100%',
    },
  },
  toggler: {
    width: '100%',
    justifyContent: 'space-between',
    paddingRight: theme.spacing(3),
    paddingTop: theme.spacing(1),
    paddingLeft: theme.spacing(6.625),
    marginLeft: 0,
  },
  activeContractItem: {
    position: 'relative',
    '& .MuiTypography-root': {
      fontWeight: 700,
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      width: theme.spacing(0.5),
      backgroundColor: '#EF2828',
    },
  },
  noPadding: {
    padding: 0,
  },
}))

const ContractsListItemComponent: FC<{
  contract: BankableEntityContractType
  isSelected: boolean
  handleChangeContract: (contract: BankableEntityContractType) => Promise<void>
  rejected?: boolean
}> = ({ contract, isSelected, handleChangeContract, rejected = false }) => {
  const { id, owner, status, ownerType } = contract
  const { t } = useTranslation()
  const user = useContext(CurrentUserContext)
  const classes = useCompanySelectUnionStyles()

  const { docsCount, refetchDocsCount } = useTotalUnreadDocCount(id, rejected)
  const { data: signaturesCount } = useActionSignatureRequestsCountQuery({
    variables: { contractId: id, statuses: [ActionSignatureStatus.Pending] },
    skip: rejected,
  })

  const name =
    ownerType === UboType.NaturalPerson ? (
      <b>{t('personal')}</b>
    ) : (
      (owner as LegalEntity).companyName
    )

  const activeContract = isActiveContract(contract, user as Individual)
  const { isLimitedPerson } = useLimitedPerson(contract)

  const isHiddenDot = useMemo(() => {
    if (!user?.isVerified || isLimitedPerson || !activeContract) {
      return true
    }

    return !isSelected ? !(docsCount || signaturesCount?.actionSignatureRequestsCount) : !docsCount
  }, [isLimitedPerson, activeContract, isSelected, docsCount, signaturesCount, user])

  const handleContractChange = useCallback(() => {
    handleChangeContract(contract).then()
  }, [handleChangeContract, contract])

  useEffect(() => {
    !rejected && void refetchDocsCount()
  }, [])

  return (
    <ListItem
      button
      onClick={handleContractChange}
      disabled={dashboardClosedStatuses.includes(contract.status as ContractStatusType)}
      className={clsx(classes.item, isSelected && classes.activeContractItem)}
    >
      <Typography className={clsx(classes.companyName, !activeContract && classes.noPadding)}>
        <ContractAlertDot size={8} top={3} hidden={isHiddenDot} />
        {name}
      </Typography>

      {!activeContract && status && (
        <Box className={clsx(`roundedPill ${displayStatusKeys[status]}`)}>
          {t(displayStatusKeys[status])}
        </Box>
      )}
    </ListItem>
  )
}

export const ContractsListItem = React.memo(ContractsListItemComponent, isEqual)
